import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup, NgForm, ValidatorFn, Validators } from '@angular/forms';
import { Observable, Subscriber, Subscription, timer } from 'rxjs';

// models
import { ALL_FILTER_ITEM } from '../../../../core/components/filters-panel/filters-panel.component';
import { checkMultiFilter, setSelected } from '../../../../core/components/filters-panel/models/filter';
import { COLORS_PRESETS } from '../../../../core/models/colorPickerPresets';
import { Filter, FilterID, FilterItem, FilterType } from '../../../../core/components/filters-panel/models/filter';
import { GlobalSettings } from '../../../../core/models/globalSettings';
import { IConfirmModalAnswer } from '../../../../core/modals/confirm-modal/models/confirmModalAnswer';
import { IConfirmModalData } from '../../../../core/modals/confirm-modal/models/confirmModalData';
import { IPlanogramPlanData, IPlanPlanogramsCreateData } from '../../../../planogram/models/planogramData';
import { MultiTreeSelectingStrategy, TreeCheckingStrategy } from '../../../../core/components/filters-panel/models/strategy';
import { ServiceFeatures } from '../../../../core/models/serviceFeatures';
import { Stand, StandType } from '../../../models/stand';

// components
import { ConfirmModalComponent } from '../../../../core/modals/confirm-modal/confirm-modal.component';
import { PanelHeaderTitle } from '../../../../core/services/side-panel.service';

// services
import { FiltersService } from '../../../../core/components/filters-panel/filters.service';
import { PlanogramsService } from '../../../../core/services/planograms.service';
import { SessionService } from '../../../../core/services/session.service';
import { SidePanelService } from '../../../../core/services/side-panel.service';
import { Utils } from '../../../../core/services/utils.service';

type PlanogramConfigurationForm = {
	name: FormControl<string>;
	number: FormControl<string>;
	type: FormControl<StandType>;
	fontSize: FormControl<number>;
	textColor: FormControl<string>;
	borderColor: FormControl<string>;
	backgroundColor: FormControl<string>;
	angle: FormControl<number>;
	categories: FormControl<Array<number>>;
	plans?: FormControl<Array<number>>;
}

export interface IPlanogramConfigResult {
	planograms: Array<Stand>;
	createdPlanPlanograms: Array<IPlanPlanogramsCreateData>;
	deletedPlanPlanograms: Array<number>;
}

@Component({
    selector: 'app-planogram-configuration',
    templateUrl: './planogram-configuration.component.html',
    styleUrls: ['./planogram-configuration.component.scss'],
    standalone: false
})
export class PlanogramConfigurationComponent implements OnInit, OnDestroy {

	public categoriesFilter: Filter;
	public colorsPresets: Array<string> = COLORS_PRESETS;
	public expanded: { first: boolean; second: boolean; third: boolean };
	public formGroup: FormGroup<PlanogramConfigurationForm>;
	public isDisabledAnimation: boolean = true;
	public isMultiSettings: boolean = false;
	public plansFilter: Filter;
	public planograms: Array<Stand>;
	public planogramTypes: Array<{ value: StandType; translate: string; }>;
	public shopMarkersDisplay = signal<string>('');
	public subscriptions: Array<Subscription> = [];

	protected readonly MULTI_SETTINGS_LABEL: string = GlobalSettings.MULTI_SETTINGS_LABEL;
	protected readonly MORE_SYMBOL_VALIDATION: number = GlobalSettings.MORE_SYMBOL_VALIDATION;

	@Input() planId: number;
	@Input() set planShopMarkers(value: Array<string>) {
		this._planShopMarkers = value ?? [];
		if (this.planograms?.length) this.loadShopMarkers();
	}
	get planShopMarkers(): Array<string> {
		return this._planShopMarkers;
	}
	private _planShopMarkers: Array<string> = [];
	@Input() windowHeight: number;
	@Input() hidePersonalization: boolean = false;
	@Input('planograms') set updatePlanograms(value: Array<Stand>) {
		this.init(value);
	}

	@Output() apply: EventEmitter<IPlanogramConfigResult> = new EventEmitter<IPlanogramConfigResult>();
	@Output() removePlanogramConnection: EventEmitter<Stand> = new EventEmitter<Stand>();
	@Output() editEquipment: EventEmitter<{ planogram: Stand; planId: number; }> = new EventEmitter<{ planogram: Stand; planId: number; }>();

	@ViewChild('form') formElement: NgForm;

	private get _angle(): number {
		if (this.isMultiSettings) {
			const sameAngle: boolean = this.planograms
				.map((planogram: Stand) => planogram.angle)
				.every((angle: number, index: number, array: Array<number>) => angle === array[0]);

			return sameAngle ? this.planograms[0].angle || 0 : null;
		}

		return this.planograms[0].angle || 0;
	}

	private get _backgroundColor(): string {
		if (this.isMultiSettings) {
			const sameBackground: boolean = this.planograms
				.map((planogram: Stand) => planogram.backgroundColor)
				.every((color: string, index: number, array: Array<string>) => color === array[0]);

			return sameBackground ? this.planograms[0].backgroundColor : null;
		}

		return this.planograms[0].backgroundColor;
	}

	private get _borderColor(): string {
		if (this.isMultiSettings) {
			const sameBorder: boolean = this.planograms
				.map((planogram: Stand) => planogram.colorBorder)
				.every((color: string, index: number, array: Array<string>) => color === array[0]);

			return sameBorder ? this.planograms[0].colorBorder : null;
		}

		return this.planograms[0].colorBorder;
	}

	private get _categories(): Array<number> {
		return Utils.to1DArray(this.planograms.map((planogram: Stand) => planogram.categories));
	}

	private get _fontSize(): number {
		if (this.isMultiSettings) {
			const sameFontSize: boolean = this.planograms
				.map((planogram: Stand) => planogram.fontSize)
				.every((size: number, index: number, array: Array<number>) => size === array[0]);

			return sameFontSize ? this.planograms[0].fontSize : null;
		}

		return this.planograms[0].fontSize;
	}

	private get _name(): string {
		if (this.isMultiSettings) {
			const sameName: boolean = this.planograms
				.map((planogram: Stand) => planogram.name)
				.every((name: string, index: number, array: Array<string>) => name === array[0]);

			return sameName ? this.planograms[0].name : null;
		}

		return this.planograms[0].name;
	}

	private get _number(): string {
		if (this.isMultiSettings) {
			const sameNumber: boolean = this.planograms
				.map((planogram: Stand) => planogram.number)
				.every((number: string, index: number, array: Array<string>) => number === array[0]);

			return sameNumber ? this.planograms[0].number : null;
		}

		return this.planograms[0].number;
	}

	private get _plans(): Array<number> {
		return Utils.to1DArray(this.planograms.map((planogram: Stand) => planogram.planogramStand.map(ps => ps.planogram)));
	}

	private get _isPlansFilter(): boolean {
		return this.planograms.some((p: Stand) => p.planogramStand.length) && !this.planId;
	}

	private get _textColor(): string {
		if (this.isMultiSettings) {
			const sameTextColor: boolean = this.planograms
				.map((planogram: Stand) => planogram.fontColor)
				.every((color: string, index: number, array: Array<string>) => color === array[0]);

			return sameTextColor ? this.planograms[0].fontColor : null;
		}

		return this.planograms[0].fontColor;
	}

	private get _type(): StandType {
		if (this.isMultiSettings) {
			const sameType: boolean = this.planograms
				.map((planogram: Stand) => planogram.type)
				.every((type: StandType, index: number, array: Array<string>) => type === array[0]);

			return sameType ? this.planograms[0].type : null;
		}

		return this.planograms[0].type;
	}

	constructor(
		private dialog: MatDialog,
		private filtersService: FiltersService,
		private panelService: SidePanelService,
		private planogramsService: PlanogramsService,
		private sessionService: SessionService
	) {
		this.expanded = { first: true, second: false, third: false };
		this.planogramTypes = [
			{ value: StandType.BASIC, translate: 'COMMON.BASIC_PLANOGRAM' },
			{ value: StandType.ADDITIONAL, translate: 'COMMON.ADDITIONAL_PLANOGRAM' }
		];
	}

	ngOnInit(): void {
		this.panelService.title = PanelHeaderTitle.PlanogramConfiguration;

		/* crutch for stopped mat expansion panel closed animation on init panel */
		timer(500).subscribe(() => this.isDisabledAnimation = false);

		this.subscriptions.push(
			this.panelService.applySubject.subscribe(() => {
				this.formElement.onSubmit(this.formGroup.value as any);
			}),
			this.panelService.closeSubject.subscribe(() => {
				this.expanded = { first: true, second: false, third: false };
				this.init(this.planograms);
			})
		);

		if (this.sessionService.hasFeature(ServiceFeatures.Categories)) {
			this.filtersService
				.getCategories({ ids: Utils.to1DArray(this.planograms.map((p: Stand) => p.categories)).join(',') })
				.subscribe((data: Array<FilterItem>) => {
					this.categoriesFilter = {
						id: FilterID.Categories,
						type: FilterType.CheckboxTree,
						param: 'categories',
						label: 'CATEGORIES',
						displayOrder: 1,
						data: [{ ...ALL_FILTER_ITEM, id: this.sessionService.defaults.root_category, children: data }],
						selectingStrategy: new MultiTreeSelectingStrategy(this.planograms.map((p: Stand) => p.categories)),
						checkingStrategy: new TreeCheckingStrategy(),
						loadChildren: (id: number) => this.filtersService.getCategoryChildren(id),
						searchQuery: '',
						search: (filter: Filter) => {
							return new Observable<Array<FilterItem>>((subscriber: Subscriber<Array<FilterItem>>) => {
								if (filter.searchQuery.length) {
									this.filtersService
										.getCategories({ search: filter.searchQuery })
										.subscribe((tree: Array<FilterItem>) => {
											subscriber.next(tree.length ? [{ ...ALL_FILTER_ITEM, children: tree }] : []);
										});
								} else {
									this.filtersService
										.getCategories({
											ids: filter.selectingStrategy.getSelected()
												.filter((id: number) => id != -1)
												.join(',')
										})
										.subscribe((tree: Array<FilterItem>) => {
											subscriber.next(tree.length ? [{ ...ALL_FILTER_ITEM, children: tree }] : []);
										});
								}
							});
						}
					};

					setSelected(
						this.categoriesFilter,
						this.categoriesFilter.data,
						Utils.to1DArray(this.planograms.map((planogram: Stand) => planogram.categories))
					);

					checkMultiFilter(
						this.categoriesFilter,
						this.categoriesFilter.data,
						this.planograms.map((planogram: Stand) => planogram.categories)
					);
				});
		}
	}

	applyPanel(): void {
		this.panelService.disabledApply = this.formElement.submitted && this.formElement.invalid;
		if (!this.formGroup.controls.angle.value && !this.isMultiSettings) {
			this.formGroup.controls.angle.setValue(0);
		}
		if (this.formElement.invalid) {
			if (this.formGroup.controls.name.invalid || this.formGroup.controls.number.invalid) {
				this.expanded = { first: true, second: false, third: false };
			} else {
				this.expanded = {
					first: false,
					second: this.formGroup.controls.plans.valid,
					third: this.formGroup.controls.plans.invalid
				};
			}
			return;
		}
		const value = this.formGroup.value;
		const deletedPlanPlanograms: Array<number> = [];
		const createdPlanPlanograms: Array<IPlanPlanogramsCreateData> = [];

		this.planograms.forEach((planogram: Stand) => {
			if (this._isPlansFilter) {
				planogram.planogramStand.forEach((data: IPlanogramPlanData) => {
					if (!this.formGroup.controls.plans.value.includes(data.planogram)) deletedPlanPlanograms.push(data.id);
				});
				const planPlanogramsIds: Array<number> = planogram.planogramStand.map(p => p.planogram);
				this.formGroup.controls.plans.value.forEach((planId: number) => {
					if (!planPlanogramsIds.includes(planId)) {
						createdPlanPlanograms.push({
							position_x: 100,
							position_y: 100,
							rotation: 0,
							plan_id: planId,
							planogram_id: planogram.main_stand
						})
					}
				});
			}

			if (value.name?.trim()) planogram.name = value.name.trim();
			if (this.formGroup.controls.number.dirty) planogram.number = value.number || null;
			if (value.type) planogram.type = value.type;
			if (value.fontSize !== null) planogram.fontSize = value.fontSize;
			if (value.textColor) planogram.fontColor = value.textColor;
			if (value.borderColor) planogram.colorBorder = value.borderColor;
			if (value.backgroundColor) planogram.backgroundColor = value.backgroundColor;
			if (value.angle !== null) planogram.angle = value.angle;

			planogram.categories = planogram.categories
				.filter((category: number) => value.categories.includes(category));
			value.categories.forEach((category: number) => {
				if (
					this.categoriesFilter.checkingStrategy.getKey(category).checked &&
					!planogram.categories.includes(category) &&
					planogram.categories.every((cat: number) => {
						const findTreeElement: FilterItem = Utils.findTreeElementById<FilterItem>(
								this.categoriesFilter.data,
								cat
							),
							childrenIds: Array<number> = Utils.findTreeElementChildrenIds(findTreeElement);

						return !childrenIds.includes(category);
					})
				) {
					planogram.categories.push(category);
				}
			});
		});

		this.apply.emit({
			planograms: this.planograms,
			deletedPlanPlanograms: deletedPlanPlanograms,
			createdPlanPlanograms: createdPlanPlanograms
		});
	}

	init(planograms: Array<Stand>): void {
		if (
			this.categoriesFilter &&
			!Utils.equalLists(planograms.map((p: Stand) => p.localId), this.planograms.map((p: Stand) => p.localId)) &&
			this.sessionService.hasFeature(ServiceFeatures.Categories)
		) {
			this.filtersService
				.getCategories({ ids: Utils.to1DArray(planograms.map((p: Stand) => p.categories)).join(',') })
				.subscribe((data: Array<FilterItem>) => {
					this.categoriesFilter.selectingStrategy.clear();
					this.categoriesFilter.checkingStrategy.clear();
					this.categoriesFilter.data = [{
						...ALL_FILTER_ITEM,
						id: this.sessionService.defaults.root_category,
						children: data
					}];
					this.categoriesFilter.selectingStrategy = new MultiTreeSelectingStrategy(
						planograms.map((planogram: Stand) => planogram.categories)
					);
					setSelected(
						this.categoriesFilter,
						this.categoriesFilter.data,
						Utils.to1DArray(planograms.map((planogram: Stand) => planogram.categories))
					);
					checkMultiFilter(
						this.categoriesFilter,
						this.categoriesFilter.data,
						planograms.map((planogram: Stand) => planogram.categories)
					);
				});
		} else {
			if (this.categoriesFilter && this.sessionService.hasFeature(ServiceFeatures.Categories)) {
				setSelected(
					this.categoriesFilter,
					this.categoriesFilter.data,
					Utils.to1DArray(planograms.map((planogram: Stand) => planogram.categories))
				);
				checkMultiFilter(
					this.categoriesFilter,
					this.categoriesFilter.data,
					planograms.map((planogram: Stand) => planogram.categories)
				);
			}
		}

		if (this.plansFilter) {
			setSelected(
				this.plansFilter,
				this.plansFilter.data,
				Utils.to1DArray(planograms.map((planogram: Stand) => planogram.planogramStand.map(ps => ps.planogram)))
			);

			checkMultiFilter(
				this.plansFilter,
				this.plansFilter.data,
				planograms.map((planogram: Stand) => planogram.planogramStand.map(ps => ps.planogram))
			);
		}

		this.isMultiSettings = planograms.length > 1;

		this.planograms = planograms.map((planogram: Stand) => {
			const result: Stand = planogram.copy();
			result.localId = planogram.localId;
			return result;
		});

		if (this._isPlansFilter && !this.plansFilter) {
			this.filtersService.getPlans({ locked: false }).subscribe((data: Array<FilterItem>) => {
				this.plansFilter = {
					id: FilterID.StorePlans,
					type: FilterType.CheckboxTree,
					param: 'plans',
					label: 'COMMON.ENTITY.STORE_PLANS',
					infoLabel: 'COMMON.PLANOGRAM.SELECT_PLANS_HINT',
					displayOrder: 1,
					data: data,
					selectingStrategy: new MultiTreeSelectingStrategy(
						this.planograms.map((planogram: Stand) => planogram.planogramStand.map(ps => ps.planogram))
					),
					checkingStrategy: new TreeCheckingStrategy(),
					searchQuery: '',
					search: (filter: Filter) => this.filtersService.commonSearch(filter, data, false)
				};

				setSelected(
					this.plansFilter,
					this.plansFilter.data,
					Utils.to1DArray(this.planograms.map((planogram: Stand) => planogram.planogramStand.map(ps => ps.planogram)))
				);

				checkMultiFilter(
					this.plansFilter,
					this.plansFilter.data,
					this.planograms.map((planogram: Stand) => planogram.planogramStand.map(ps => ps.planogram))
				);
			});
		}

		this.setFormGroup();
		this.loadShopMarkers();
	}

	loadShopMarkers(): void {
		if (!this.planograms?.length) {
			this.shopMarkersDisplay.set('');
			return;
		}
		const mainPlanogramIds = [...new Set(this.planograms.map((p: Stand) => p.main_stand).filter((id: number) => id != null))];
		if (!mainPlanogramIds.length) {
			this.shopMarkersDisplay.set(this._planShopMarkers.join('; ') || '');
			return;
		}
		this.planogramsService.getShopMarkers(mainPlanogramIds).subscribe((data) => {
			const apiMarkers = this.isMultiSettings
				? data.flatMap((d) => d.shop_markers ?? [])
				: (data.find((r) => r.main_planogram_id === this.planograms[0]?.main_stand)?.shop_markers ?? []);
			const allMarkers = [...new Set([...apiMarkers, ...this._planShopMarkers])].filter(Boolean);
			this.shopMarkersDisplay.set(allMarkers.join('; ') || '');
		});
	}

	numberInputValidate(event: KeyboardEvent): boolean {
		return !['e', 'E', '-', '+', '.', ','].includes(event.key);
	}

	onCategoryCheck(): void {
		this.formGroup.controls.categories.setValue(this.categoriesFilter.selectingStrategy.getSelected());
	}

	onPlanCheck(): void {
		this.formGroup.controls.plans.setValue(this.plansFilter.selectingStrategy.getSelected());
	}

	removeConnection(): void {
		if (this.isMultiSettings) return;
		this.removePlanogramConnection.emit(this.planograms[0]);
	}

	setFormGroup(): void {
		const nameValidators: Array<ValidatorFn> = [Validators.maxLength(this.MORE_SYMBOL_VALIDATION)],
			fontSizeValidators: Array<ValidatorFn> = [Validators.min(1), Validators.pattern('\\d+')];

		if (!this.isMultiSettings) {
			nameValidators.push(Validators.required);
			fontSizeValidators.push(Validators.required);
		}
		this.formGroup = new FormGroup<PlanogramConfigurationForm>({
			name: new FormControl<string>(this._name, nameValidators),
			number: new FormControl<string>(this._number, [
				Validators.pattern(/^[\p{L}0-9\-_\/#]+$/u),
				Validators.maxLength(50)
			]),
			type: new FormControl<StandType>(this._type),
			fontSize: new FormControl<number>(this._fontSize, fontSizeValidators),
			textColor: new FormControl<string>(this._textColor),
			borderColor: new FormControl<string>(this._borderColor),
			backgroundColor: new FormControl<string>(this._backgroundColor),
			angle: new FormControl<number>(this._angle, [
				Validators.pattern(/(?!-)\d+/),
				Validators.min(-360),
				Validators.max(360)
			]),
			categories: new FormControl<Array<number>>(this._categories)
		});

		if (this._isPlansFilter) {
			this.formGroup.addControl('plans', new FormControl<Array<number>>(this._plans, [Validators.required]));
		}

		this.formGroup.valueChanges.subscribe(() => {
			this.panelService.disabledApply = this.formElement.submitted && this.formElement.invalid;
		});
		if (this.formElement) this.formElement.onReset();
		this.panelService.disabledApply = true;
	}

	showEditEquipmentWarning(): void {
		if (this.isMultiSettings) return;

		this.dialog
			.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'EDIT_INSTRUMENT_WARNING_MESSAGE',
					confirmLabel: 'YES',
					cancelLabel: 'NO',
					twoAnswers: true
				}
			})
			.afterClosed()
			.subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer) {
					this.editEquipment.emit({ planogram: this.planograms[0], planId: this.planId });
				}
			});
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}
}